// src/components/auth/ResetPasswordPage.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Header from "../shared/Header";
import { api } from "../../services/api";

/* ───────── Utilidades ───────── */
function estimateStrength(pw) {
  let s = 0;
  if (!pw) return 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(4, s);
}

function StrengthPills({ value }) {
  return (
    <div className="mt-2">
      <div className="flex gap-1.5" role="progressbar" aria-valuemin={0} aria-valuemax={4} aria-valuenow={value}>
        {Array.from({ length: 5 }).map((_, i) => (
          <span key={i} className={`h-1.5 flex-1 rounded-full transition-all ${i <= value ? "bg-emerald-600" : "bg-gray-200"}`} />
        ))}
      </div>
      <p className="mt-1 text-xs text-gray-600" aria-live="polite">
        {["Muy débil", "Débil", "Aceptable", "Buena", "Fuerte"][value]}
      </p>
    </div>
  );
}

function PolicyChecklist({ pw, serverDifferentOk }) {
  const rules = useMemo(
    () => [
      { ok: pw.length >= 8, label: "Mínimo 8 caracteres" },
      { ok: /[A-Z]/.test(pw) && /[a-z]/.test(pw), label: "Mayúsculas y minúsculas" },
      { ok: /\d/.test(pw), label: "Al menos un número" },
      { ok: /[^A-Za-z0-9]/.test(pw), label: "Al menos un símbolo" },
      // En restablecimiento por enlace no podemos verificarlo en cliente
      { ok: serverDifferentOk, label: "Distinta a la actual (se valida al guardar)" },
    ],
    [pw, serverDifferentOk]
  );
  return (
    <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm" aria-live="polite">
      {rules.map((r, i) => (
        <li key={i} className="flex items-center gap-2">
          <span
            className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] ${
              r.ok ? "bg-emerald-600 text-white" : "bg-gray-200 text-gray-500"
            }`}
            aria-hidden="true"
          >
            {r.ok ? "✓" : "•"}
          </span>
          <span className={r.ok ? "text-gray-700" : "text-gray-500"}>{r.label}</span>
        </li>
      ))}
    </ul>
  );
}

/* ───────── Página ───────── */
export default function ResetPasswordPage() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || sp.get("t") || "";
  const navigate = useNavigate();

  const [pwd1, setPwd1] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(false);
  const [error, setError] = useState("");
  const [serverDifferentOk, setServerDifferentOk] = useState(false);

  useEffect(() => {
    if (!token) navigate("/login", { replace: true });
  }, [token, navigate]);

  const strength = estimateStrength(pwd1);
  const meetsPolicies =
    pwd1.length >= 8 &&
    /[A-Z]/.test(pwd1) &&
    /[a-z]/.test(pwd1) &&
    /\d/.test(pwd1) &&
    /[^A-Za-z0-9]/.test(pwd1);

  const valid = token && pwd1 && pwd2 && pwd1 === pwd2 && meetsPolicies && strength >= 2 && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setSaving(true);
    setError("");
    setServerDifferentOk(false);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd1 });
      setServerDifferentOk(true); // si el servidor acepta, inferimos que no era igual a la actual
      setOk(true);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "No se pudo restablecer la contraseña. Revisa el enlace.";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="min-h-screen relative"
      style={{
        backgroundImage: "url('/fondo.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/10 to-transparent" />

      <Header
        title="Fichajes"
        subtitle="Define una nueva contraseña"
        showBack
        onBack={() => navigate("/login")}
      />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <section className="min-h-[70vh] grid place-items-start md:place-items-center py-8 sm:py-10">
          <div className="relative w-full max-w-xl">
            {/* Spotlight */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -inset-8 rounded-[32px] blur-3xl opacity-60"
              style={{
                background:
                  "radial-gradient(120px 120px at 20% 10%, rgba(0,75,135,.12), transparent 60%), radial-gradient(160px 160px at 90% 20%, rgba(56,189,248,.10), transparent 60%)",
              }}
            />

            <section
              aria-labelledby="rp-title"
              className="relative rounded-2xl border border-white/60 bg-white/90 backdrop-blur p-6 sm:p-8 shadow-[0_10px_30px_rgba(0,0,0,0.08)]"
            >
              <h2 id="rp-title" className="text-2xl font-bold tracking-tight text-gray-900">
                Restablecer contraseña
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Introduce una contraseña nueva que cumpla las políticas de seguridad.
              </p>

              {ok ? (
                <div className="mt-5 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 p-4" role="status" aria-live="polite">
                  ¡Contraseña actualizada correctamente!
                  <div className="mt-4 flex flex-wrap gap-3">
                    <Link
                      to="/login"
                      className="inline-flex items-center justify-center rounded-lg px-4 py-2 font-semibold text-white bg-[#004B87] hover:bg-[#003B6D] transition"
                    >
                      Ir a iniciar sesión
                    </Link>
                    <Link
                      to="/"
                      className="inline-flex items-center justify-center rounded-lg px-4 py-2 font-semibold border border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
                    >
                      Volver al inicio
                    </Link>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-5 space-y-5" noValidate>
                  {/* Nueva */}
                  <label className="block">
                    <span className="text-sm text-gray-700">Nueva contraseña</span>
                    <div className="mt-1 relative">
                      <input
                        type={show1 ? "text" : "password"}
                        value={pwd1}
                        onChange={(e) => setPwd1(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pr-10 outline-none focus:ring-2 focus:ring-[#004B87]"
                        autoComplete="new-password"
                        minLength={8}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShow1((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-600"
                        title={show1 ? "Ocultar" : "Mostrar"}
                        aria-label={show1 ? "Ocultar nueva contraseña" : "Mostrar nueva contraseña"}
                      >
                        {show1 ? "🙈" : "👁️"}
                      </button>
                    </div>
                    <StrengthPills value={strength} />
                    <PolicyChecklist pw={pwd1} serverDifferentOk={serverDifferentOk} />
                  </label>

                  {/* Repetir */}
                  <label className="block">
                    <span className="text-sm text-gray-700">Repite la contraseña</span>
                    <div className="mt-1 relative">
                      <input
                        type={show2 ? "text" : "password"}
                        value={pwd2}
                        onChange={(e) => setPwd2(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pr-10 outline-none focus:ring-2 focus:ring-[#004B87]"
                        autoComplete="new-password"
                        minLength={8}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShow2((v) => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-600"
                        title={show2 ? "Ocultar" : "Mostrar"}
                        aria-label={show2 ? "Ocultar repetición de contraseña" : "Mostrar repetición de contraseña"}
                      >
                        {show2 ? "🙈" : "👁️"}
                      </button>
                    </div>
                    {pwd1 && pwd2 && pwd1 !== pwd2 && (
                      <p className="text-sm text-red-600 mt-1" role="alert">Las contraseñas no coinciden.</p>
                    )}
                  </label>

                  {error && (
                    <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 p-3" role="alert">
                      {error}
                    </div>
                  )}

                  <div className="pt-1">
                    <button
                      type="submit"
                      disabled={!valid}
                      className={`w-full inline-flex items-center justify-center rounded-lg px-4 py-2 font-semibold text-white transition-all ${
                        valid ? "bg-[#004B87] hover:translate-y-[-1px] hover:shadow" : "bg-blue-300 cursor-not-allowed"
                      }`}
                    >
                      {saving ? (
                        <>
                          <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" opacity="0.25" />
                            <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="4" fill="none" />
                          </svg>
                          Cambiando…
                        </>
                      ) : (
                        "Cambiar contraseña"
                      )}
                    </button>
                  </div>
                </form>
              )}
            </section>
          </div>
        </section>
      </main>
    </div>
  );
}

